import { useEffect, useRef, useState } from "react";
import { useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Mic, MonitorSpeaker, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  type CaptureResult,
  type CaptureSource,
  captureErrorMessage,
  readStoredSource,
  startCapture,
  stopCapture,
  writeStoredSource,
} from "./audioCapture";
import { startChunking } from "./audioChunking";
import { sendAudioChunk } from "./copilotTranscribe";
import {
  hasActiveTranscriptConsent,
  stopTranscript,
  useTranscriptConsent,
} from "./transcriptConsent";
import { HelpTooltip } from "../help/HelpTooltip";

const SOURCES: { id: CaptureSource; label: string; Icon: typeof Mic }[] = [
  { id: "mic", label: "Micrófono", Icon: Mic },
  { id: "tab", label: "Audio de pestaña/sistema", Icon: MonitorSpeaker },
  { id: "both", label: "Ambos", Icon: Waves },
];

/**
 * Captura de audio + transcripción real (Bloque C2, pasos 3-4): corta la captura en
 * fragmentos de ~25s (`audioChunking`) y los manda a `copilot_transcribe`
 * (`copilotTranscribe`), que descuenta el cupo del paso 1 y agrega el texto al registro del
 * consentimiento. El audio vive solo en memoria del navegador y se descarta al enviarse; nunca
 * se guarda en disco, storage ni base de datos. Se detiene siempre al desmontar (salir de la
 * llamada, `dealId` cambia) o si el cupo llega al freno del 80%.
 */
export const AudioCaptureCard = ({ dealId }: { dealId: number }) => {
  const { data: consent } = useTranscriptConsent(dealId);
  const given = hasActiveTranscriptConsent(consent);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<CaptureSource>(() => readStoredSource());
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captureRef = useRef<CaptureResult | null>(null);
  const meterCleanupRef = useRef<() => void>(() => {});
  const chunkingStopRef = useRef<() => void>(() => {});
  const levelBarRef = useRef<HTMLDivElement>(null);

  const stopLocal = () => {
    meterCleanupRef.current();
    chunkingStopRef.current();
    stopCapture(captureRef.current);
    captureRef.current = null;
    setCapturing(false);
    if (levelBarRef.current) levelBarRef.current.style.width = "0%";
  };

  const stop = async () => {
    stopLocal();
    if (consent && !consent.stopped_at) {
      try {
        await stopTranscript(consent.id);
        queryClient.invalidateQueries({
          queryKey: ["transcript_consent", dealId],
        });
      } catch (e) {
        notify((e as Error).message, { type: "error" });
      }
    }
  };

  // Nunca deja captura viva: al desmontar (salir de la llamada, cambiar de página) o si el
  // botón "Detener transcripción" del aviso (paso 2) se presiona en otro lado.
  useEffect(() => () => stopLocal(), []);
  useEffect(() => {
    if (consent?.stopped_at && capturing) stopLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent?.stopped_at]);

  const changeSource = (id: CaptureSource) => {
    setSource(id);
    writeStoredSource(id);
  };

  const onChunk = async (blob: Blob, durationSeconds: number) => {
    if (!consent) return;
    const r = await sendAudioChunk(consent.id, blob, durationSeconds);
    if (r.text != null)
      queryClient.setQueryData(
        ["transcript_consent", dealId],
        (old: typeof consent | undefined) =>
          old ? { ...old, transcript_text: r.text } : old,
      );
    if (r.fatal) {
      notify(r.message ?? "Se detuvo la captura", { type: "warning" });
      await stop();
    } else if (r.message) {
      notify(r.message, { type: "error" });
    }
  };

  const start = async () => {
    if (!consent) return;
    setError(null);
    try {
      const result = await startCapture(source);
      captureRef.current = result;
      setCapturing(true);
      meterCleanupRef.current = startLevelMeter(result.stream, (level) => {
        if (levelBarRef.current)
          levelBarRef.current.style.width = `${Math.round(level * 100)}%`;
      });
      chunkingStopRef.current = startChunking(result.stream, onChunk);
    } catch (e) {
      setError(captureErrorMessage(e));
    }
  };

  return (
    <Card className="gap-2 p-3 text-sm">
      <div className="flex items-center gap-2">
        <b>Captura de audio</b>
        <HelpTooltip topic="calls.capture" />
        {capturing && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--critical)]">
            <span className="size-2 animate-pulse rounded-full bg-[var(--critical)]" />
            Capturando
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SOURCES.map(({ id, label, Icon }) => (
          <Button
            key={id}
            size="sm"
            variant={source === id ? "default" : "outline"}
            disabled={capturing}
            onClick={() => changeSource(id)}
          >
            <Icon />
            {label}
          </Button>
        ))}
      </div>
      <div
        className={cn(
          "h-2 overflow-hidden rounded-full bg-secondary",
          !capturing && "opacity-0",
        )}
      >
        <div ref={levelBarRef} className="h-full w-0 rounded-full bg-primary" />
      </div>
      {error && <p className="text-[var(--critical)]">{error}</p>}
      {capturing ? (
        <Button
          size="sm"
          variant="outline"
          className="self-start"
          onClick={stop}
        >
          Detener captura
        </Button>
      ) : (
        <Button
          size="sm"
          className="self-start"
          disabled={!given}
          onClick={start}
        >
          Iniciar captura
        </Button>
      )}
      {!given && (
        <p className="text-xs text-muted-foreground">
          Marca la casilla de autorización de arriba para poder iniciar la
          captura.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        El audio se transcribe en fragmentos de ~25s y se descarta de inmediato:
        nunca se guarda audio, solo el texto en el registro de esta llamada.
      </p>
    </Card>
  );
};

/** Medidor de nivel en vivo (AnalyserNode). Devuelve una función para detenerlo. */
const startLevelMeter = (
  stream: MediaStream,
  onLevel: (level0to1: number) => void,
): (() => void) => {
  const ctx = new AudioContext();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  ctx.createMediaStreamSource(stream).connect(analyser);
  const data = new Uint8Array(analyser.frequencyBinCount);
  let raf = 0;
  const loop = () => {
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    onLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
  return () => {
    cancelAnimationFrame(raf);
    void ctx.close();
  };
};
